import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { useAuth } from "./App";
import CandidateProfileCard from "./CandidateProfileCard";
import CandidateApplicationsSection from "./CandidateApplicationsSection";
import CandidateAvailablePositions from "./CandidateAvailablePositions";

import { API_URL } from "./config";

export default function CandidatePortal({ onError }) {
  const { auth, fetchWithAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(0);

  useEffect(() => {
    async function fetchProfile() {
      setLoading(true);
      setError("");
      try {
        const res = await fetchWithAuth(`${API_URL}/candidates/me`);
        if (!res.ok) {
          throw new Error("Failed to fetch candidate profile");
        }
        const data = await res.json();
        setProfile(data);
      } catch (err) {
        setError(err.message || "Unable to load profile");
        if (onError) onError(err);
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, [fetchWithAuth, auth, onError]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Typography variant="h6" sx={{ mt: 4 }}>
        No candidate profile found.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 6,
        background: "linear-gradient(135deg,#f5f7fb,#eef2ff)",
      }}
    >
      <Container maxWidth="md">
        {/* Profile */}
        <Box sx={{ mb: 4 }}>
          <CandidateProfileCard profile={profile} />
        </Box>

        {/* Tabs + content card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            px: { xs: 2, sm: 4 },
            py: 3,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(246,248,252,0.98))",
            boxShadow: "0 18px 45px rgba(15,23,42,0.12)",
            border: "1px solid rgba(148,163,184,0.25)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              centered
              textColor="primary"
              TabIndicatorProps={{ sx: { display: "none" } }}
              sx={{
                minHeight: 0,
                "& .MuiTab-root": {
                  textTransform: "none",
                  minHeight: 0,
                  fontSize: 14,
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 999,
                  mr: 1,
                  color: "#6b7280",
                  "&.Mui-selected": {
                    backgroundColor: "#111827",
                    color: "#f9fafb",
                  },
                },
                "& .MuiTabs-flexContainer": {
                  backgroundColor: "rgba(243,244,246,0.9)",
                  borderRadius: 999,
                  padding: 0.5,
                },
              }}
            >
              <Tab label="My applications" />
              <Tab label="Available positions" />
            </Tabs>
          </Box>

          <Box sx={{ mt: 3 }}>
            {tab === 0 && (
              <CandidateApplicationsSection applications={profile.applications} />
            )}
            {tab === 1 && <CandidateAvailablePositions />}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
